"use client";

import { useRef } from "react";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Award,
  BarChart3,
  BrainCircuit,
  Briefcase,
  CalendarClock,
  CheckCircle2,
  GraduationCap,
  MessageCircle,
  Target,
  Zap,
} from "lucide-react";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  GOOGLE_MAPS_DIRECTIONS_URL,
  SITE_WHATSAPP_URL_DEFAULT,
} from "@/lib/site-config";
import { cn } from "@/lib/utils";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const PILLARS = [
  {
    icon: BrainCircuit,
    title: "Industry-expert trainers",
    description:
      "Every program is led by professional data scientists who translate real project experience into classroom clarity.",
    accent: "text-mont-violet",
  },
  {
    icon: BarChart3,
    title: "Hands-on from day one",
    description:
      "Structured assignments, live datasets, and portfolio-grade dashboards — not slide-only theory.",
    accent: "text-mont-orange",
  },
  {
    icon: Briefcase,
    title: "100% placement assistance",
    description:
      "Resume reviews, interview prep, and counseling aligned with analyst and data science hiring loops.",
    accent: "text-emerald-500",
  },
  {
    icon: Award,
    title: "ISO 9001:2015 certified",
    description:
      "Quality management systems audited for academic education, professional training, and skill development.",
    accent: "text-yellow-500",
  },
  {
    icon: CalendarClock,
    title: "Flexible batch timings",
    description:
      "Weekday and weekend options built for working professionals balancing office hours and upskilling.",
    accent: "text-sky-400",
  },
  {
    icon: Target,
    title: "Brochure-aligned curriculum",
    description:
      "Python, ML, BI, SAS, SQL, and Excel tracks mapped to Mont2 program fees and outcomes site-wide.",
    accent: "text-fuchsia-400",
  },
];

const JOURNEY = [
  {
    step: "01",
    title: "Learn the foundations",
    body: "Statistics, Python, SQL, and visualization basics — paced for beginners and career switchers alike.",
  },
  {
    step: "02",
    title: "Build real projects",
    body: "Capstone-style work with datasets that mirror business questions hiring managers actually ask.",
  },
  {
    step: "03",
    title: "Present with confidence",
    body: "Story-driven dashboards and model walkthroughs you can demo in interviews and portfolio reviews.",
  },
  {
    step: "04",
    title: "Launch your next role",
    body: "Placement support, mock interviews, and counselor guidance through batch enrollment to job search.",
  },
];

const AUDIENCE = [
  {
    icon: GraduationCap,
    title: "Students & fresh graduates",
    points: [
      "Foundation-to-placement tracks with structured modules",
      "Portfolio projects you can showcase on LinkedIn",
      "EMI-friendly enrollment on major programs",
    ],
  },
  {
    icon: Briefcase,
    title: "Working professionals",
    points: [
      "Evening and weekend batch flexibility",
      "Upskill from analyst to scientist without quitting your job",
      "Tooling aligned to Big 4 and product-company interview loops",
    ],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.55,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  }),
};

export default function AboutPage() {
  const rootRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      gsap.from(".about-hero > *", {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: "power3.out",
      });

      gsap.utils.toArray<HTMLElement>(".about-section").forEach((section) => {
        const targets = section.querySelectorAll(".about-reveal");
        if (!targets.length) return;

        gsap.fromTo(
          targets,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.7,
            stagger: 0.08,
            ease: "power3.out",
            scrollTrigger: {
              trigger: section,
              start: "top 85%",
              once: true,
            },
          },
        );
      });
    },
    { scope: rootRef },
  );

  return (
    <div ref={rootRef} className="site-page">
      <Navbar />

      <main className="relative z-10 flex-1 pt-20">
        {/* Hero */}
        <section className="site-section relative flex min-h-[65vh] items-center justify-center overflow-hidden bg-background pb-16 pt-12 md:min-h-[70vh]">
          <div className="pointer-events-none absolute inset-0 z-0 bg-grid opacity-50 mask-image-gradient dark:opacity-20" />
          <div className="pointer-events-none absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-mont-violet/20 blur-[140px] dark:bg-mont-violet/35" />

          <div className="site-container relative z-10 text-center">
            <div className="about-hero mx-auto flex max-w-4xl flex-col items-center gap-6">
              <Badge variant="outline" className="border-mont-violet/30 text-mont-violet">
                Our Mission
              </Badge>
              <h1 className="font-heading text-5xl font-bold leading-tight tracking-tight text-foreground drop-shadow-2xl md:text-7xl">
                Empowering minds for{" "}
                <span className="text-gradient font-semibold">tomorrow&apos;s data careers</span>
              </h1>
              <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                MONT2 Academy blends rigorous analytics training with the rhythm of modern product teams —
                structured, practical, and built for professionals who want results, not just certificates.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                <Link href="/courses">
                  <Button className="h-12 rounded-xl border-0 bg-gradient-primary px-8 text-white hover:opacity-90">
                    Explore programs
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link href={SITE_WHATSAPP_URL_DEFAULT} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="h-12 rounded-xl border-border bg-background/60 backdrop-blur-sm">
                    <MessageCircle className="mr-2 h-4 w-4 text-[#22c55e]" />
                    WhatsApp us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="about-section border-y border-border bg-card/30 py-16 md:py-24">
          <div className="site-container">
            <div className="about-reveal mx-auto mb-12 max-w-3xl text-center">
              <Badge variant="outline" className="mb-4 border-mont-violet/30 text-mont-violet">
                Why Choose Us
              </Badge>
              <h2 className="font-heading text-3xl font-bold text-foreground md:text-5xl">
                Built for <span className="text-gradient">career switchers</span> and ambitious learners
              </h2>
            </div>

            <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-14">
              <div className="about-reveal space-y-5 text-lg leading-relaxed text-muted-foreground">
                <p>
                  At MONT2 Academy, we are dedicated to shaping the next generation of data-driven innovators. We
                  believe in practical, hands-on learning designed specifically for working professionals and dedicated
                  students.
                </p>
                <p>
                  All our courses are taught by professional Data Scientists who bring real-world industry experience
                  into the classroom. We don&apos;t just teach theory—we ensure you are ready to apply your skills on day
                  one of your new career.
                </p>
                <p className="font-semibold text-foreground">
                  Switch to a better career today and join thousands of students navigating the future of AI and Data
                  Analytics with us.
                </p>
                <p>
                  From Python programming and machine learning to Tableau, Power BI, SAS, and SQL — our roadmap follows
                  the Mont2 brochure so every fee, EMI option, and module you see on the site matches what you discuss
                  with our counselors.
                </p>
                <p>
                  Whether you are a fresher building your first portfolio or a working analyst upskilling for a senior
                  role, our Mehrauli classroom combines live mentoring, flexible batches, and placement-focused
                  assignments that mirror how analytics teams actually deliver insights.
                </p>
              </div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="grid gap-4 sm:grid-cols-2"
              >
                {PILLARS.slice(0, 4).map((item, i) => (
                  <motion.div
                    key={item.title}
                    custom={i}
                    variants={fadeUp}
                    className="group rounded-2xl border border-border/80 bg-background/60 p-5 backdrop-blur-md transition-all duration-500 hover:-translate-y-1 hover:border-mont-violet/40 hover:shadow-[0_20px_50px_-20px_rgba(139,92,246,0.35)]"
                  >
                    <item.icon className={cn("mb-3 h-7 w-7", item.accent)} />
                    <h3 className="mb-2 font-heading text-base font-bold text-foreground">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Pillars grid */}
        <section className="about-section relative overflow-hidden py-16 md:py-24">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-mont-violet/10 via-transparent to-transparent" />
          <div className="site-container relative">
            <div className="about-reveal mx-auto mb-10 max-w-2xl text-center">
              <Badge variant="outline" className="mb-4 border-mont-orange/35 text-mont-orange">
                What sets us apart
              </Badge>
              <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                The MONT2 <span className="text-gradient">difference</span>
              </h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {PILLARS.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.45 }}
                  className="group relative overflow-hidden rounded-3xl border border-border/80 bg-card/40 p-7 backdrop-blur-sm transition-all duration-500 hover:border-mont-violet/35 hover:shadow-[0_24px_60px_-24px_rgba(139,92,246,0.3)]"
                >
                  <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-mont-violet/10 blur-2xl transition-transform duration-700 group-hover:scale-150" />
                  <item.icon className={cn("relative mb-4 h-8 w-8", item.accent)} />
                  <h3 className="relative mb-2 font-heading text-lg font-bold text-foreground">{item.title}</h3>
                  <p className="relative text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Journey — no image, all steps visible */}
        <section className="about-section border-y border-border bg-muted/20 py-16 md:py-20">
          <div className="site-container">
            <div className="about-reveal mx-auto mb-10 max-w-3xl text-center">
              <Badge variant="outline" className="mb-4 border-mont-violet/40 text-mont-violet">
                How we train
              </Badge>
              <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                From first lesson to <span className="text-gradient">job-ready</span>
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Our teaching loop mirrors how high-performing analytics teams ship work — learn, build, present, and
                iterate with mentor feedback at every stage.
              </p>
            </div>

            <div className="mx-auto grid max-w-3xl gap-4">
              {JOURNEY.map((item, i) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.06, duration: 0.45 }}
                  className="flex gap-5 rounded-2xl border border-border/70 bg-background/60 p-5 transition-colors hover:border-mont-violet/30 hover:bg-card/60"
                >
                  <span className="font-heading text-2xl font-bold text-mont-violet">{item.step}</span>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Who we serve */}
        <section className="about-section py-16 md:py-20">
          <div className="site-container">
            <div className="about-reveal mx-auto mb-8 max-w-2xl text-center">
              <Badge variant="outline" className="mb-4 border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                Who we serve
              </Badge>
              <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                Programs for every stage of your data journey
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {AUDIENCE.map((card, i) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.08, duration: 0.45 }}
                  className="rounded-3xl border border-border/80 bg-gradient-to-br from-card/80 to-background p-8 shadow-sm"
                >
                  <card.icon className="mb-4 h-10 w-10 text-mont-violet" />
                  <h3 className="mb-4 font-heading text-xl font-bold text-foreground">{card.title}</h3>
                  <ul className="space-y-3">
                    {card.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="about-section pb-20 pt-2">
          <div className="site-container">
            <div className="about-reveal relative overflow-hidden rounded-3xl border border-mont-violet/30 bg-gradient-to-br from-mont-violet/20 via-card to-mont-orange/15 p-10 text-center shadow-[0_0_60px_-12px_rgba(139,92,246,0.45)] md:p-14">
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-mont-orange/25 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -left-12 h-64 w-64 rounded-full bg-mont-violet/30 blur-3xl" />
              <div className="relative mx-auto max-w-2xl space-y-6">
                <Zap className="mx-auto h-10 w-10 text-mont-orange" />
                <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                  Ready to write your <span className="text-gradient">next chapter</span> with MONT2?
                </h2>
                <p className="text-lg text-muted-foreground">
                  Browse INR-priced courses, check the upcoming batch countdown, or visit our Mehrauli centre — the same
                  counseling team guides you from enquiry to enrollment.
                </p>
                <div className="flex flex-wrap justify-center gap-3 pt-2">
                  <Link href="/upcoming-batch">
                    <Button className="h-12 rounded-xl border-0 bg-gradient-primary px-8 text-white hover:opacity-90">
                      View upcoming batch
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link href="/courses">
                    <Button variant="outline" className="h-12 rounded-xl border-border bg-background/60">
                      All courses
                    </Button>
                  </Link>
                  <Link href={GOOGLE_MAPS_DIRECTIONS_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" className="h-12 rounded-xl">
                      Get directions
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
